import { useState } from "react";
import { AlertTriangle, Check, Code, Copy, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useApiKeys } from "@/hooks/useApiKeys";
import { ApiKeyWithSecret } from "@/types/api-keys";

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const DevelopersSection = () => {
  const { apiKeys, isLoading, createKey, revokeKey } = useApiKeys();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState<ApiKeyWithSecret | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      toast.error("Please enter a name for your API key");
      return;
    }

    setIsCreating(true);
    try {
      const key = await createKey(newKeyName.trim());
      setCreatedKey(key);
      setIsCreateOpen(false);
      setNewKeyName("");
    } catch (err: any) {
      toast.error(err.message || "Failed to create API key");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      toast.success("API key copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleRevoke = async (keyId: string, keyName: string) => {
    if (
      !confirm(`Revoke "${keyName}"? Any requests using this key will stop working immediately.`)
    ) {
      return;
    }

    setRevokingId(keyId);
    try {
      await revokeKey(keyId);
      toast.success("API key revoked");
    } catch (err: any) {
      toast.error(err.message || "Failed to revoke API key");
    } finally {
      setRevokingId(null);
    }
  };

  const activeKeys = apiKeys.filter((k) => k.is_active);
  const revokedKeys = apiKeys.filter((k) => !k.is_active);

  return (
    <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-[18px]">
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
            <Code className="h-5 w-5 text-genie-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">API Keys</h2>
            <p className="text-xs text-[#7C8B9C]">
              Use API keys to authenticate requests to the HelpGenie API
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsCreateOpen(true)}
          className="text-white border-none rounded-md px-3.5 py-[7px] font-semibold cursor-pointer text-sm bg-genie-primary hover:bg-genie-primary/90 transition-colors"
        >
          Create API Key
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-genie-primary" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && apiKeys.length === 0 && (
        <div className="text-center py-8">
          <Code className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-[#7C8B9C] text-sm">No API keys yet</p>
          <p className="text-[#7C8B9C] text-xs mt-1">Create an API key to start making requests</p>
        </div>
      )}

      {/* Active keys */}
      {!isLoading && activeKeys.length > 0 && (
        <div className="space-y-2">
          {activeKeys.map((key) => (
            <div
              key={key.id}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm text-[#222B45]">{key.name}</p>
                </div>
                <p className="text-xs text-[#7C8B9C] font-mono mt-0.5">
                  hg_live_{key.key_prefix}••••••••
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Created {formatDate(key.created_at)}
                  {key.last_used_at
                    ? ` · Last used ${formatDate(key.last_used_at)}`
                    : " · Never used"}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-3">
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                  Active
                </span>
                <button
                  onClick={() => handleRevoke(key.id, key.name)}
                  disabled={revokingId === key.id}
                  className="text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                >
                  {revokingId === key.id ? "Revoking..." : "Revoke"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Revoked keys */}
      {!isLoading && revokedKeys.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Revoked</p>
          <div className="space-y-2">
            {revokedKeys.map((key) => (
              <div
                key={key.id}
                className="flex items-center justify-between p-3 border border-gray-100 rounded-lg opacity-60"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm text-gray-500">{key.name}</p>
                  </div>
                  <p className="text-xs text-gray-400 font-mono mt-0.5">
                    hg_live_{key.key_prefix}••••••••
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Revoked {key.revoked_at ? formatDate(key.revoked_at) : ""}
                  </p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
                  Revoked
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Key Sheet */}
      <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <SheetContent
          side="right"
          showClose={false}
          className="w-full sm:max-w-md flex flex-col h-full p-0"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">Create API Key</h3>
            <button
              onClick={() => setIsCreateOpen(false)}
              className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleCreate} className="flex-1 px-6 py-5">
            <label htmlFor="key-name" className="block mb-1.5 font-medium text-sm">
              Key Name
            </label>
            <input
              id="key-name"
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder='e.g. "Production", "Development", "Testing"'
              className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
              maxLength={100}
              autoFocus
            />
            <p className="text-xs text-[#7C8B9C] mt-2">
              Give your key a descriptive name so you can identify it later.
            </p>
          </form>

          {/* Footer */}
          <div className="px-6 py-4 border-t">
            <button
              onClick={handleCreate as any}
              disabled={isCreating || !newKeyName.trim()}
              className="w-full text-white border-none rounded-md px-3.5 py-2.5 font-semibold cursor-pointer text-sm bg-genie-primary hover:bg-genie-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isCreating && <Loader2 className="h-4 w-4 animate-spin" />}
              {isCreating ? "Creating..." : "Create API Key"}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Show Created Key Sheet */}
      <Sheet open={!!createdKey} onOpenChange={() => setCreatedKey(null)}>
        <SheetContent
          side="right"
          showClose={false}
          className="w-full sm:max-w-md flex flex-col h-full p-0"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">Your API Key</h3>
            <button
              onClick={() => setCreatedKey(null)}
              className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-5">
            {/* Warning */}
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">
                Copy your API key now. You won't be able to see it again.
              </p>
            </div>

            {/* Key display */}
            <label className="block mb-1.5 font-medium text-sm">{createdKey?.name}</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm break-all select-all">
                {createdKey?.key}
              </div>
              <button
                onClick={handleCopy}
                className="shrink-0 h-10 w-10 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-genie-primary" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-500" />
                )}
              </button>
            </div>

            {/* Usage example */}
            <div className="mt-6">
              <p className="font-medium text-sm mb-2">Usage</p>
              <div className="p-3 bg-gray-900 rounded-lg font-mono text-xs text-gray-300 overflow-x-auto">
                <span className="text-gray-500">Authorization:</span> Bearer{" "}
                {createdKey?.key?.slice(0, 20)}...
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t">
            <button
              onClick={() => setCreatedKey(null)}
              className="w-full text-white border-none rounded-md px-3.5 py-2.5 font-semibold cursor-pointer text-sm bg-genie-primary hover:bg-genie-primary/90 transition-colors"
            >
              Done
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
